/**
 * Mobile view of the cut-to-size modal (below the `lg` breakpoint): a scrolling panel that slides
 * in below the page header, with back and close controls, the product image and stock
 * attributes, the quantity, one input or dropdown per cut field, and Apply.
 *
 * Rendering only. All state, options and validation come from `DefineCutModal.tsx` through
 * `DefineCutProps`; `DefineCutModalDesktop.tsx` renders the same props for larger screens, and a
 * field change usually belongs in both.
 *
 * Behaviour that differs from desktop:
 * - The panel opens and closes by toggling the `slide-open` / `slide-close` classes of
 *   `components/Helper/commonStyles.tsx` → `SliderMenu`. While open, the page scrolls to the top
 *   and the body gets `overflow: hidden`; on close the body's earlier `style` is restored.
 * - On mount it calls `handleClose`, which also sends the GA `CUT_TO_SIZE` `CLOSE` event.
 * - Disabled cut shapes and processes stay in the dropdown options (desktop removes them).
 * - In the native app the panel starts below the safe area and leaves more room under Apply.
 */
import { useTheme } from "@ryerson/frontend.theme";
import React, { createRef, RefObject, useEffect, useState } from "react";
import { DefineCutProps } from "./DefineCutModal";
import { Button } from "@ryerson/frontend.button";
import { Typography } from "@ryerson/frontend.typography";
import { SliderMenu } from "@components/Helper/commonStyles";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Icon } from "@ryerson/frontend.assets";
import { LanguageToLocale, useWindowSize } from "@ryerson/frontend.common";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import rgba from "polished/lib/color/rgba";
import { Divider } from "@components/CustomerDashboard/Helpers/StyledComponents";
import { Dropdown, InputType, NumberInput } from "@ryerson/frontend.form-controls";
import {
	CutAttribute,
	GenericDropDownType,
	displayProductShapeForm,
	getCompanyLink,
} from "./DefineCutHelper";
import { inch } from "@ryerson/common.uom";
import { Tooltip } from "@ryerson/frontend.modal";
import AttributeIcon from "@components/Shared/AttributeIcon/AttributeIcon";
import { useApplication } from "@ryerson/frontend.application";
import { Alert } from "@ryerson/frontend.notification";
import { Link } from "@ryerson/frontend.navigation";
import { UpdatedProductAttribute } from "@components/Shared/ProductDisplay/Helpers/ProductDisplay.models";
import { NetworkOperationResult } from "@components/constants";
import { getAttributeNameFromArray } from "@utils/translateAttributes";

const TooltipContent = styled(Typography)`
	margin: 10px 0px;
	width: 50vw;
`;

const closeFiltersPane = (props: any) => {
	const { theme } = props;
	return css`
		height: 50px;
		width: 50px;
		border-left: 1px solid ${rgba(theme.colors.primary.white, 0.1)};
	`;
};

const GlobalNavigation = styled.div`
	height: 50px;
	width: 100%;
	top: 0;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${theme.colors.primary.lightGray};
			background-color: ${theme.colors.primary.darkGray};
		`;
	}}
`;

const StyledInput = styled(NumberInput)`
	width: 100%;
`;

/** Mobile cut-to-size panel; see the file header. */
const DefineCutModalMobile: React.FC<DefineCutProps> = ({
	showDefineCutModal,
	handleClose,
	content,
	product,
	customSubtitle,
	cutAttributes,
	language,
	defineCutParameters,
	updateInputValue,
	updateDropdownValue,
	cutOptions,
	finishOptions,
	processOptions,
	toleranceOptions,
	handleApply,
	formSubmitted,
	validateCutParameters,
	imageSource,
	helpText,
	productDetailsAPIFetchStatus,
	productCanBeCut,
}) => {
	const { theme } = useTheme();
	const { isNative } = useApplication();
	const { background, gray, lighterGray, link, header } = theme.colors.primary;

	const locale = LanguageToLocale[language];

	const [domNodeRefs] = useState<any>({});
	const [hasMounted, setHasMounted] = React.useState<boolean>(false);
	const [bodyStyles, setBodyStyles] = useState<string>("");
	const windowSize = useWindowSize();

	const creatDomNodeRef = (fieldName: string): RefObject<any> => {
		domNodeRefs[fieldName] = createRef();
		return domNodeRefs[fieldName];
	};

	// Manage slider animation and body scrolling
	// Flips both classes on every change of `showDefineCutModal` after mount, which keeps them in
	// step with the prop only because the panel is first rendered closed (`slide-close`).
	useEffect(() => {
		if (hasMounted) {
			const sliderEl = domNodeRefs["product-details-define-cut"].current;

			sliderEl?.classList?.toggle("slide-open");
			sliderEl?.classList?.toggle("slide-close");

			const body = document?.querySelector("body");

			if (showDefineCutModal) {
				(window as any).scrollTo(0, 0);
				setBodyStyles(body?.getAttribute("style") || "");
				setTimeout(() => {
					body?.setAttribute("style", "overflow: hidden;");
				}, 100);
			} else {
				body && body.setAttribute("style", bodyStyles);
			}
		}
	}, [showDefineCutModal]);

	// On mount: closes the modal (which also sends the GA `CLOSE` event) and applies the same
	// body scroll handling as the effect above.
	useEffect(() => {
		setHasMounted(true);
		handleClose();

		const body = document?.querySelector("body");

		if (showDefineCutModal) {
			(window as any).scrollTo(0, 0);
			setBodyStyles(body?.getAttribute("style") || "");
			setTimeout(() => {
				body?.setAttribute("style", "overflow: hidden;");
			}, 100);
		} else {
			body && body.setAttribute("style", bodyStyles);
		}
	}, []);

	const renderTooltip = (tooltipContent: string) => {
		return (
			<FlexItem>
				<Tooltip type="secondary" icon="help" size="sm">
					<TooltipContent size="xs" type="primary">
						{tooltipContent}
					</TooltipContent>
				</Tooltip>
			</FlexItem>
		);
	};

	const renderCutAttributeDropdown = (
		attribute: string,
		label: string,
		value: string | number,
		options: GenericDropDownType[],
		required: boolean
	): JSX.Element => {
		return (
			<Dropdown
				label={label}
				size="md"
				value={value}
				onChange={(value: string) => updateDropdownValue(value, attribute)}
				options={options}
				language={language}
				required={required}
				labelAddition={
					attribute === "processing" && helpText?.process && helpText?.process !== "" ? (
						renderTooltip(helpText?.process)
					) : attribute === "finish" && helpText?.finish && helpText?.finish !== "" ? (
						renderTooltip(helpText?.finish)
					) : attribute === "cutShape" &&
					  helpText?.cutShape &&
					  helpText?.cutShape !== "" ? (
						renderTooltip(helpText?.cutShape)
					) : (
						<></>
					)
				}
				dataTestId="define-cut-modal-mobile-dropdown"
			/>
		);
	};

	const renderCutAttributeInputField = (
		attribute: string,
		label: string,
		value: string | number,
		inputType: InputType,
		isRequired: boolean,
		cutAttribute: CutAttribute
	): JSX.Element => {
		return (
			<>
				<StyledInput
					label={label}
					name={attribute}
					placeholder={""}
					required={isRequired}
					value={value || ""}
					onChange={(event) => updateInputValue(event)}
					language={language}
					inputType={inputType}
					isValid={cutAttribute.isValid}
					disabled={cutAttribute.disabled}
					submitted={formSubmitted}
					validation={() => {
						validateCutParameters();
						return cutAttribute.error;
					}}
					precision={4}
					labelAddition={<AttributeIcon attribute={attribute} />}
				/>
			</>
		);
	};

	return (
		<ContentSection vPadding="0" hPadding="0">
			<SliderMenu
				{...{ theme: theme }}
				ref={creatDomNodeRef(`product-details-define-cut`)}
				className="product-details-define-cut slide-close"
				css={{ top: isNative ? "calc(env(safe-area-inset-top) + 60px)" : 100 }}
			>
				<Flex
					css={{
						height: windowSize[1] - 100,
						overflowY: "scroll",
					}}
					direction="column"
				>
					<GlobalNavigation theme={theme}>
						<Flex alignItems="center">
							<FlexItem grow={1}>
								<Flex justifyContent="flex-start" onClick={handleClose}>
									<FlexItem alignSelf="center" css={{ marginLeft: 20 }}>
										<Icon
											icon="chevron-left"
											size="md"
											onClick={handleClose}
											color={background}
											circled={true}
											css={{ borderColor: gray }}
										/>
									</FlexItem>
									<FlexItem
										css={css`
											padding-left: 10px;
										`}
									>
										<Typography
											variant="p"
											color={theme.colors.tertiary.label}
											size="md"
										>
											{content?.back}
										</Typography>
									</FlexItem>
								</Flex>
							</FlexItem>
							<FlexItem>
								<Flex
									style={closeFiltersPane({ theme: theme })}
									justifyContent="center"
									alignItems="center"
								>
									<Icon
										icon="close"
										size="sm"
										onClick={handleClose}
										color={theme.colors.primary.white}
									/>
								</Flex>
							</FlexItem>
						</Flex>
					</GlobalNavigation>
					<Flex
						alignItems="flex-start"
						css={{ padding: "27px 22px 13px", width: "100%", gap: 14 }}
						direction="column"
					>
						<Typography css={{ marginBottom: 6 }} variant="h3">
							{content?.cutToSize}
						</Typography>
						<Typography size="lg">{customSubtitle}</Typography>
						<Typography size="lg">
							{
								product?.itemDescriptionLocalization?.[LanguageToLocale[language]]
									?.shortDescription
							}
						</Typography>
					</Flex>
					<Flex
						css={{ height: 202, background: background, marginBottom: 30 }}
						justifyContent="center"
					>
						<img
							css={{ background: background, padding: "20px" }}
							src={imageSource}
							alt=""
						/>
					</Flex>
					<Flex
						direction="column"
						css={{ gap: 10, padding: "0px 22px", marginBottom: 45 }}
					>
						<Typography size="md" weight="bold">
							{displayProductShapeForm(product, language)}
							<Divider theme={theme} css={{ marginTop: 9 }} />
						</Typography>
						{product?.attributes?.map((attribute, index, attributesCollection) => {
							const attributeName =
								"name" in attribute
									? attribute.name
									: attribute.localization?.[locale]?.name || "";
							return (
								<FlexItem key={index}>
									<Flex justifyContent="space-between" key={index}>
										<Flex alignItems="baseline">
											<Typography
												size="md"
												weight="bold"
												css={{ marginRight: 5 }}
											>
												{attributeName}
											</Typography>
											<AttributeIcon
												attribute={getAttributeNameFromArray(
													attributesCollection,
													index,
													locale
												)}
											/>
										</Flex>
										<Typography size="md">
											{
												(attribute as UpdatedProductAttribute)
													?.localization?.[locale]?.value
											}
										</Typography>
									</Flex>
									{index !== attributesCollection.length - 1 ? (
										<Divider theme={theme} css={{ marginTop: 6 }} />
									) : null}
								</FlexItem>
							);
						})}
					</Flex>
					{/* If request is not in flight and product cannot be cut, display error message */}
					{productDetailsAPIFetchStatus !== NetworkOperationResult.PENDING &&
						!productCanBeCut && (
							<Flex
								direction="column"
								wrap="wrap"
								css={{
									background: background,
									padding: "20px",
									gap: 30,
									width: "100%",
								}}
							>
								<Alert severity="error" size="md">
									<Typography size="md" type={"primary"}>
										{content.errors.processMessage1}
										<Link
											next={false}
											target="_blank"
											to={`${getCompanyLink()}`}
											color={link}
											size="md"
										>
											{content.errors.companyLink.replace(
												"%url%",
												getCompanyLink()
											)}
										</Link>
										{content.errors.processMessage2}
										<Link
											next={false}
											to={`mailTo:${content.errors.brandedEmail}`}
											color={link}
											size="md"
										>
											{content.errors.brandedEmail}
										</Link>
										{content.errors.processMessage3}
									</Typography>
								</Alert>
							</Flex>
						)}

					{/* Only display cut input boxes if request is not in flight and this product has cut attributes */}
					{productDetailsAPIFetchStatus !== NetworkOperationResult.PENDING &&
						cutAttributes?.length > 0 && (
							<>
								<Flex
									css={{
										background: background,
										padding: "20px",
										paddingBottom: "0",
										gap: "15px",
										width: "100%",
									}}
									direction="column"
								>
									<Typography size="md" weight="bold" color={header}>
										{content.cutTo}
									</Typography>
									<StyledInput
										label={content.quantityPcs}
										name="quantity"
										placeholder={""}
										required={true}
										value={defineCutParameters.quantity}
										onChange={(event) => updateInputValue(event)}
										language={language}
										labelAddition={
											<Flex css={{ marginLeft: "10px" }}>
												{renderTooltip(content.quantityTooltipMessage)}
											</Flex>
										}
										submitted={formSubmitted}
										isValid={
											formSubmitted
												? Boolean(defineCutParameters.quantity)
												: true
										}
										precision={4}
										dataTestId="define-cut-modal-mobile-quantity-number-input"
									/>
								</Flex>
								<Flex
									direction="column"
									alignItems="flex-start"
									css={{
										background: background,
										padding: "20px",
										gap: 30,
										width: "100%",
									}}
								>
									{cutAttributes.map(
										(cutAttribute: CutAttribute, index: number) => {
											return (
												<FlexItem css={{ width: "100%" }} key={index}>
													{cutAttribute.name ===
														content.cutAttributes.shape.value &&
														renderCutAttributeDropdown(
															"cutShape",
															content.cutAttributes.shape.display,
															defineCutParameters?.cutShape?.value,
															cutOptions,
															true
														)}
													{cutAttribute.name ===
														content.cutAttributes.processing.value &&
														renderCutAttributeDropdown(
															"processing",
															content.cutAttributes.processing
																.display,
															defineCutParameters?.processing?.value,
															processOptions,
															true
														)}
													{cutAttribute.name ===
														content.cutAttributes.finish.value &&
														renderCutAttributeDropdown(
															"finish",
															content.cutAttributes.finish.display,
															defineCutParameters?.finish?.value,
															finishOptions,
															false
														)}
													{cutAttribute.name ===
														content.cutAttributes.width.value &&
														renderCutAttributeInputField(
															"width",
															`${content.cutAttributes.width.display}, ${inch.display}`,
															defineCutParameters?.width,
															"number",
															true,
															cutAttribute
														)}
													{cutAttribute.name ===
														content.cutAttributes.length.value &&
														renderCutAttributeInputField(
															"length",
															`${content.cutAttributes.length.display}, ${inch.display}`,
															defineCutParameters?.length,
															"number",
															true,
															cutAttribute
														)}
													{cutAttribute.name ===
														content.cutAttributes.oneSide.value &&
														renderCutAttributeInputField(
															"oneSide",
															`${content.cutAttributes.oneSide.display}, ${inch.display}`,
															defineCutParameters?.oneSide,
															"number",
															true,
															cutAttribute
														)}
													{cutAttribute.name ===
														content.cutAttributes.oneSideTolerance
															.value &&
														renderCutAttributeDropdown(
															"oneSideTolerance",
															content.cutAttributes.oneSideTolerance
																.display,
															defineCutParameters?.oneSideTolerance
																?.value,
															toleranceOptions,
															true
														)}
													{cutAttribute.name ===
														content.cutAttributes.tolerance.value &&
														renderCutAttributeDropdown(
															"tolerance",
															content.cutAttributes.tolerance.display,
															defineCutParameters?.tolerance?.value,
															toleranceOptions,
															true
														)}
													{cutAttribute.name ===
														content.cutAttributes.outsideDiameter
															.value &&
														renderCutAttributeInputField(
															"outsideDiameter",
															`${content.cutAttributes.outsideDiameter.display}, ${inch.display}`,
															defineCutParameters?.outsideDiameter,
															"number",
															true,
															cutAttribute
														)}
													{cutAttribute.name ===
														content.cutAttributes.outsideTolerance
															.value &&
														renderCutAttributeDropdown(
															"outsideTolerance",
															content.cutAttributes.outsideTolerance
																.display,
															defineCutParameters?.outsideTolerance
																?.value,
															toleranceOptions,
															true
														)}
													{cutAttribute.name ===
														content.cutAttributes.insideDiameter
															.value &&
														renderCutAttributeInputField(
															"insideDiameter",
															`${content.cutAttributes.insideDiameter.display}, ${inch.display}`,
															defineCutParameters?.insideDiameter,
															"number",
															true,
															cutAttribute
														)}
													{cutAttribute.name ===
														content.cutAttributes.insideTolerance
															.value &&
														renderCutAttributeDropdown(
															"insideTolerance",
															content.cutAttributes.insideTolerance
																.display,
															defineCutParameters?.insideTolerance
																?.value,
															toleranceOptions,
															true
														)}
													{cutAttribute.name ===
														content.cutAttributes.longSide.value &&
														renderCutAttributeInputField(
															"longSide",
															`${content.cutAttributes.longSide.display}, ${inch.display}`,
															defineCutParameters?.longSide,
															"number",
															true,
															cutAttribute
														)}
													{cutAttribute.name ===
														content.cutAttributes.shortSide.value &&
														renderCutAttributeInputField(
															"shortSide",
															`${content.cutAttributes.shortSide.display}, ${inch.display}`,
															defineCutParameters?.shortSide,
															"number",
															true,
															cutAttribute
														)}
													{cutAttribute.name ===
														content.cutAttributes.widthLengthTolerance
															.value &&
														renderCutAttributeDropdown(
															"widthLengthTolerance",
															content.cutAttributes
																.widthLengthTolerance.display,
															defineCutParameters
																?.widthLengthTolerance?.value,
															toleranceOptions,
															true
														)}
												</FlexItem>
											);
										}
									)}
								</Flex>
								<Flex
									justifyContent="space-between"
									alignItems="center"
									css={{
										background: lighterGray,
										width: "100%",
										padding: "10px 22px",
										marginBottom: isNative ? 80 : 40,
									}}
								>
									<Button
										size="lg"
										label={content.apply}
										onClick={handleApply}
										fullwidth={true}
										css={{
											width: "100%",
											padding: "10px 0",
											fontWeight: "bold",
										}}
										disabled={cutAttributes.length === 0}
										dataTestId="define-cut-modal-mobile-apply-button"
									/>
								</Flex>
							</>
						)}
				</Flex>
			</SliderMenu>
		</ContentSection>
	);
};

export default DefineCutModalMobile;
